import AgTypography from '../../../Styles/AgTypography';
import { AgColor } from '../../../Styles/AgColor';
import { useIntl } from 'react-intl';
import AgAccountProfileCard from '../../Cards/AgAccountProfileCard';
import AgMemberProfileCard from '../../Cards/AgMemberProfileCard';
import AgButton from '../../AgButton';
import AgInfoCard from '../../Cards/AgInfoCard';
import { Dispatch, MouseEventHandler, ReactNode, SetStateAction } from 'react';
import { ApiOrganizationProfile } from '@agunity/api-v4/build/data-contracts';
import { Box } from '@mui/material';

export interface AgProfilePageNavigationProps {
  buttonOnClick?: MouseEventHandler<HTMLButtonElement>,
  infoCardTitle?: string;
  infoCardMessage?: string;
  infoCardChildren?: ReactNode;
  accountImg?: string;
  accountName?: string;
  accountID?: string;
  accountVillage?: string;
  accountUserType?: number;
  accountEmail?: string;
  accountPhone?: string;
  inactive?: boolean;
  memberData?: ApiOrganizationProfile[];
  orgData?: ApiOrganizationProfile[];
  height?: string;
  setSelectedCard?: Dispatch<SetStateAction<number>>;
  selectedCard?: number;
  hideInfoCard?: boolean;
  hideAddButton?: boolean;
  memberProfile?: number | string;
  resetPersonalDetailsToOriginal?: () => void;
  resetMemberDetailsToOriginal?: () => void;
  isDirty?: boolean;
  openDirtyModal?: boolean;
  setOpenDirtyModal?: Dispatch<SetStateAction<boolean>>;
  profileIndex?: number;
  setProfileIndex?: Dispatch<SetStateAction<number>>;
  profileUnsavedModal?: ReactNode;
  viewAvatarModal?: ReactNode;
  showSteps?: boolean;
  navigationHeaderTitle1?: string;
  navigationHeaderTitle2?: string;
  accountProfileCardTitle?: string;
  accountProfileCardIdLabel?: string;
  stepsLabels?: string[];
  hideOrgDataAndShowSettings?: boolean;
}

const AgProfilePageNavigation = (
  {
    buttonOnClick,
    infoCardTitle,
    infoCardMessage,
    infoCardChildren,
    accountImg,
    accountName,
    accountUserType,
    accountID,
    accountVillage,
    accountEmail,
    accountPhone,
    inactive,
    memberData,
    selectedCard,
    setSelectedCard,
    hideInfoCard,
    hideAddButton,
    memberProfile,
    isDirty,
    setOpenDirtyModal,
    setProfileIndex,
    profileUnsavedModal,
    viewAvatarModal,
    showSteps,
    orgData,
    navigationHeaderTitle1,
    navigationHeaderTitle2,
    accountProfileCardTitle,
    accountProfileCardIdLabel,
    stepsLabels,
    hideOrgDataAndShowSettings
  }: AgProfilePageNavigationProps
) => {
  const messages = useIntl().formatMessage;

  return (
    <Box
      sx={{
        width: '232px',
        display: 'grid',
        alignContent: 'start',
        gap: '8px',
        padding: '12px 8px',
        boxSizing: 'border-box',
        backgroundColor: AgColor.grey1,
        borderTopLeftRadius: '8px',
        borderBottomLeftRadius: '8px'
      }}
    >
      <AgTypography
        style={{
          padding: '0 0 4px 8px'
        }}
      >
        <AgTypography
          contentStyle={'AP.Assistive.Text.SemiBold'}
          message={navigationHeaderTitle1 ?? messages({ id: 'PROFILE_PAGE_NAVI_HEADER1' })}
          style={{ color: AgColor.grey7 }}
        />
        <AgTypography
          contentStyle={'AP.Assistive.Text.Regular'}
          message={navigationHeaderTitle2 ?? messages({ id: 'PROFILE_PAGE_NAVI_HEADER2' })}
          style={{ color: AgColor.grey7 }}
        />
      </AgTypography>

      {
        showSteps &&
        <AgTypography
          style={{ paddingTop: '4px', display: 'flex', gap: '10px', alignItems: 'center' }}
        >
          <AgTypography
            message={'1'}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Inter',
              fontWeight: '700',
              fontSize: '10px',
              lineHeight: '12px',
              backgroundColor: selectedCard === 0 ? AgColor.primaryLight : AgColor.primaryLight2,
              color: selectedCard === 0 ? AgColor.grey0 : AgColor.primaryLight
            }}
          />
          <AgTypography
            message={stepsLabels?.[0] ?? messages({ id: 'MEMBER_CREATE_ACCOUNT' })}
            style={{
              fontFamily: 'Inter',
              fontWeight: '700',
              fontSize: '10px',
              lineHeight: '12px'
            }}
          />
        </AgTypography>
      }

      <AgAccountProfileCard
        img={accountImg}
        name={accountName}
        accountID={accountID}
        village={accountVillage}
        email={accountEmail}
        userType={accountUserType}
        phone={accountPhone}
        inactive={inactive}
        isSelected={selectedCard === 0}
        onClick={() => {
          if (isDirty) {
            setOpenDirtyModal && setOpenDirtyModal(true);
            setProfileIndex && setProfileIndex(0);
          } else {
            setSelectedCard && setSelectedCard(0);
          }
        }}
        cardTitle={accountProfileCardTitle}
        idLabel={accountProfileCardIdLabel}
      />

      {
        hideOrgDataAndShowSettings && showSteps &&
        <AgTypography
          style={{ paddingTop: '4px', display: 'flex', gap: '10px', alignItems: 'center' }}
        >
          <AgTypography
            message={'2'}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Inter',
              fontWeight: '700',
              fontSize: '10px',
              lineHeight: '12px',
              backgroundColor: (
                selectedCard && selectedCard === 1
              )
                ? AgColor.primaryLight
                : AgColor.primaryLight2,
              color: (
                selectedCard && selectedCard === 1
              )
                ? AgColor.grey0
                : AgColor.primaryLight
            }}
          />
          <AgTypography
            message={stepsLabels?.[1] ?? messages({ id: 'MANAGE_SETTINGS' })}
            style={{
              fontFamily: 'Inter',
              fontWeight: '700',
              fontSize: '10px',
              lineHeight: '12px'
            }}
          />
        </AgTypography>
      }

      {
        !hideOrgDataAndShowSettings && showSteps &&
        <AgTypography
          style={{ paddingTop: '4px', display: 'flex', gap: '10px', alignItems: 'center' }}
        >
          <AgTypography
            message={'2'}
            style={{
              width: '24px',
              height: '24px',
              borderRadius: '60px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: 'Inter',
              fontWeight: '700',
              fontSize: '10px',
              lineHeight: '12px',
              backgroundColor: (
                selectedCard &&
                memberData &&
                selectedCard - 1 >= 0 &&
                selectedCard - 1 < memberData?.length
              )
                ? AgColor.primaryLight
                : AgColor.primaryLight2,
              color: (
                selectedCard &&
                memberData && selectedCard - 1 >= 0 &&
                selectedCard - 1 < memberData?.length
              )
                ? AgColor.grey0
                : AgColor.primaryLight
            }}
          />
          <AgTypography
            message={stepsLabels?.[1] ?? messages({ id: 'MEMBER_SET_UP_PROFILE' })}
            style={{
              fontFamily: 'Inter',
              fontWeight: '700',
              fontSize: '10px',
              lineHeight: '12px'
            }}
          />
        </AgTypography>
      }

      {!hideOrgDataAndShowSettings && orgData?.map((member, index) => {
        return (
          <AgMemberProfileCard
            key={index + 1}
            memberProfile={memberProfile ?? 0}
            memberID={memberData && memberData[0].id}
            managedBy={member?.relationships?.[0]?.orgBName || member?.name || ''}
            memberOf={member?.relationships?.[0]?.orgBName || member.name || ''}
            inactive={memberData && memberData[index]?.activeStatus !== 0}
            isSelected={selectedCard === index + 1}
            onClick={() => {
              if (isDirty) {
                setOpenDirtyModal && setOpenDirtyModal(true);
                setProfileIndex && setProfileIndex(index + 1);
              } else {
                setSelectedCard && setSelectedCard(index + 1);
              }
            }}
          />
        );
      })}

      {!hideAddButton &&
        <AgButton
          label={messages({ id: 'PROFILE_PAGE_ADD_BUTTON' })}
          color={'secondary'}
          width={'216px'}
          onClick={buttonOnClick}
        />
      }

      {!hideInfoCard &&
        <AgInfoCard
          title={infoCardTitle}
          message={infoCardMessage}
          children={infoCardChildren}
          closeIcon={false}
        />
      }

      {profileUnsavedModal}
      {viewAvatarModal}

    </Box>
  );
};

export default AgProfilePageNavigation;